import {
  Box,
  IconButton,
  InputBase,
  Typography,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  Portal,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useState, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { colors } from "@components/ui/colors";
import { SmsContact } from "../contacts/types/SmsContact";
import { useSearchVrSmsContacts } from "../contacts/hooks/useSearchVrSmsContacts";
import { formatUSPhone } from "../../../../utils";
import {
  DEFAULT_COUNTRY_CODE,
  formatRecipientLive,
  getRecipientDedupeKey,
  getRecipientDigits,
  isSameSmsRecipient,
  normalizeSmsRecipient,
  sanitizeRecipientInput,
  startsWithCountryCode,
} from "../../utils/smsRecipient";

interface SendNewSmsToContactProps {
  smsContacts: SmsContact[];
  onSelectContact: (contact: SmsContact) => void;
}

export const SendNewSmsToContact = ({ smsContacts = [], onSelectContact }: SendNewSmsToContactProps) => {
  const { company } = useSelector((state: any) => state.userState);
  const companyId = company?.id ? Number(company.id) : 0;

  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const { apiContacts, isSearching } = useSearchVrSmsContacts(companyId, searchTerm);

  const hasLetters = (val: string) => /[a-zA-Z]/.test(val);
  const isSearchingByName = hasLetters(searchTerm);

  const displayValue = isSearchingByName ? searchTerm : formatRecipientLive(searchTerm);

  const digits = getRecipientDigits(searchTerm);
  const isValidPhoneNumber = !isSearchingByName && digits.length > 0;

  const getSmsContactTitle = (contact: SmsContact) => {
    const resolved = contact.contact;
    if (resolved) {
      const firstName = (resolved as any).firstName || (resolved as any).name || "";
      const lastName = resolved.lastName || "";
      return [firstName, lastName].filter(Boolean).join(" ") || formatUSPhone(contact.phoneNumber);
    }
    return formatUSPhone(contact.phoneNumber);
  };

  const filteredContacts = useMemo(() => {
    if (searchTerm.trim() === "") return [];

    // Filter local resolved SMS contacts
    const localFiltered = smsContacts.filter((contact) => {
      const title = getSmsContactTitle(contact).toLowerCase();
      const phone = contact.phoneNumber.toLowerCase();
      const search = searchTerm.toLowerCase();
      const formattedPhone = formatUSPhone(contact.phoneNumber).toLowerCase();

      return title.includes(search) || phone.includes(search) || formattedPhone.includes(search);
    });

    const combined = [...localFiltered, ...apiContacts];
    const unique: SmsContact[] = [];
    const seen = new Set<string>();

    for (const c of combined) {
      const key = getRecipientDedupeKey(c.phoneNumber);
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(c);
      }
    }
    return unique;
  }, [smsContacts, apiContacts, searchTerm]);

  useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [open, searchTerm]);

  const handleSelectContactInternal = (contact: SmsContact) => {
    onSelectContact(contact);
    setSearchTerm("");
    setOpen(false);
  };

  const handleSend = () => {
    if (!isValidPhoneNumber) return;

    const recipient = normalizeSmsRecipient(searchTerm);

    const existing = smsContacts.find((c) => isSameSmsRecipient(c.phoneNumber, recipient));

    if (existing) {
      onSelectContact(existing);
    } else {
      onSelectContact({ phoneNumber: recipient });
    }
    setSearchTerm("");
    setOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (hasLetters(val)) {
      setSearchTerm(val);
    } else {
      setSearchTerm(sanitizeRecipientInput(val));
    }
    setOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filteredContacts.length === 1) {
        handleSelectContactInternal(filteredContacts[0]);
      } else if (isValidPhoneNumber) {
        handleSend();
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Box>
          <Box
            ref={anchorRef}
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: 1.5,
              px: 2,
              py: 1,
              transition: "border-color 0.15s",
              "&:focus-within": {
                borderBottomColor: colors.orange.base,
              },
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: colors.orange.base,
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              To
            </Typography>

            {!isSearchingByName && !startsWithCountryCode(searchTerm) && (
              <Typography
                sx={{
                  mt: 0.15,
                  fontSize: 12,
                  fontWeight: 500,
                  color: colors.orange.base,
                  flexShrink: 0,
                  lineHeight: 1,
                  userSelect: "none",
                }}
              >
                {DEFAULT_COUNTRY_CODE}
              </Typography>
            )}

            <InputBase
              placeholder="Search or enter number..."
              value={displayValue}
              onChange={handleChange}
              onFocus={() => setOpen(true)}
              onKeyDown={handleKeyDown}
              inputProps={!isSearchingByName ? { inputMode: "tel" } : undefined}
              sx={{
                flex: 1,
                fontSize: 13,
                color: "var(--fx-surface-textsecondary, #64748B)",
                "& input": { p: 0 },
                "& input::placeholder": {
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  opacity: 1,
                },
              }}
            />

            <IconButton
              type="button"
              size="small"
              disabled={!isValidPhoneNumber && filteredContacts.length !== 1}
              disableRipple
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (filteredContacts.length === 1) {
                  handleSelectContactInternal(filteredContacts[0]);
                } else if (isValidPhoneNumber) {
                  handleSend();
                }
              }}
              sx={{
                p: 0.5,
                flexShrink: 0,
                transition: "opacity 0.15s",
                opacity: isValidPhoneNumber || filteredContacts.length === 1 ? 1 : 0.25,
                color: colors.orange.base,
                "&:hover": {
                  bgcolor: "transparent",
                  color: colors.orange[300],
                },
                "&.Mui-disabled": {
                  color: "#e07428",
                },
              }}
            >
              <SendIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>

          {open && searchTerm.length > 0 && (
            <Portal>
              <Box
                sx={{
                  position: "fixed",
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                  zIndex: 2147483647,
                  maxHeight: 240,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "divider",
                    borderRadius: "3px",
                  },
                }}
              >
                {isSearching && filteredContacts.length === 0 ? (
                  <Box sx={{ px: 2, py: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <CircularProgress size={16} sx={{ mr: 1, color: colors.orange.base }} />
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Searching...</Typography>
                  </Box>
                ) : filteredContacts.length > 0 ? (
                  <List disablePadding>
                    {filteredContacts.map((contact) => {
                      const title = getSmsContactTitle(contact);
                      const hasName = contact.contact;
                      return (
                        <ListItemButton
                          key={contact.phoneNumber}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelectContactInternal(contact);
                          }}
                          sx={{
                            py: 1.5,
                            "&:hover": {
                              bgcolor: colors.orange[50],
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "text.primary",
                                }}
                              >
                                {title}
                              </Typography>
                            }
                            secondary={
                              hasName ? (
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    color: "text.secondary",
                                  }}
                                >
                                  {formatUSPhone(contact.phoneNumber)}
                                </Typography>
                              ) : undefined
                            }
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                ) : (
                  <Box sx={{ px: 2, py: 2, textAlign: "center" }}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>No contacts found for "{searchTerm}"</Typography>
                  </Box>
                )}
              </Box>
            </Portal>
          )}
        </Box>
      </ClickAwayListener>
    </Box>
  );
};
